import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Phone, Check,
  Hammer, SquareStack, PanelsTopLeft, Paintbrush, ChefHat, ShowerHead,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { PageHero } from "./Services";
import { brand, heroImage, remodeling, warranty } from "../data/site";

const icons = { Hammer, SquareStack, PanelsTopLeft, Paintbrush, ChefHat, ShowerHead };

const FAQ = [
  {
    q: "You are a roofing company. Why remodeling?",
    a: "Because the work overlaps more than it looks. Additions, patio covers, windows and stucco all come down to keeping water out of a building envelope, which is the thing we already do every day. We took on the classification that lets us finish those jobs instead of handing half of them away.",
  },
  {
    q: "Do you do the electrical and plumbing yourselves?",
    a: "No, and no residential general contractor in Arizona is allowed to. Electrical, plumbing and mechanical work goes to licensed specialty contractors. They are named on your scope before work starts, so you know exactly who is on your property and under what license.",
  },
  {
    q: "How is the estimate put together?",
    a: "The same way as a roof: line items, with every product named by manufacturer, line and color. Allowances are stated as allowances rather than buried, so you can see what is a real number and what is a placeholder you still get to choose.",
  },
  {
    q: "Do permits get handled?",
    a: "Yes. We pull the permits the work requires and schedule the inspections. Where an HOA has to approve a change to the exterior, we provide the product specifications and color samples for your submission.",
  },
  {
    q: "Is remodeling work covered by the workmanship warranty?",
    a: `Remodeling carries a ${remodeling.warrantyYears}-year written workmanship warranty, stated in your contract before work starts rather than promised on a handshake. That is separate from the ${warranty.laborYears || "25"}-year workmanship warranty, which covers roofs. A written warranty is a promise we add on top of your rights under Arizona law, not a replacement for them.`,
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      name: "Home Remodeling",
      serviceType: "Residential remodeling and repair",
      provider: { "@type": "RoofingContractor", name: brand.name, url: SITE_URL },
      areaServed: { "@type": "AdministrativeArea", name: "Maricopa County, AZ" },
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Remodeling", item: `${SITE_URL}/remodeling` },
      ],
    },
  ],
};

const Remodeling = () => {
  // The license gate. Until `remodeling.enabled` is true there is no page
  // here at all — not a "coming soon", which would still be offering the
  // work. See the note on `remodeling` in src/data/site.js.
  if (!remodeling.enabled) return <Navigate to="/services" replace />;

  return (
    <div>
      <SEO
        title="Home Remodeling in Phoenix, AZ | Pepla Roofing"
        description="Residential remodeling across Phoenix and Maricopa County — room additions, patio covers, windows, stucco, kitchens and bathrooms, with every product named in writing."
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="Beyond The Roof"
        title="Home Remodeling"
        subtitle="The same written scope, named products and photo documentation we put behind a roof — applied to the rest of the house."
        image={heroImage}
        imageAlt="Phoenix home exterior"
      />

      {/* What we take on */}
      <section className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">What We Take On</h2>
          <div className="brand-line mb-5" />
          <p className="text-muted-foreground text-lg leading-relaxed max-w-xl mb-10">
            We lead with the work that touches the building envelope, because that is where our
            crews already spend their days.
          </p>

          <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
            <div className="grid sm:grid-cols-2 gap-px">
              {remodeling.categories.map((c) => {
                const Icon = icons[c.icon] || Hammer;
                return (
                  <div key={c.title} className="bg-card flex gap-5 p-7 sm:p-8">
                    <span className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon size={18} className="text-brand-dark" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-heading text-lg uppercase tracking-wide mb-1.5">{c.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{c.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Who actually does the work — the B classification requires these to be
          subcontracted, and saying so plainly is worth more than hiding it. */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
              Who Is Actually On Your House
            </h2>
            <div className="brand-line mb-6" />
            <p className="text-white/70 leading-relaxed mb-5">
              Arizona does not let a residential general contractor self-perform certain trades,
              and we would not want to. These go to licensed specialty contractors, named on your
              scope before anyone starts, with their license numbers on the paperwork.
            </p>
            <p className="text-white/70 leading-relaxed">
              Everything else — framing, carpentry, tile, stucco, finish work and anything that
              touches the roof — is our own crew.
            </p>
          </div>
          <ul className="space-y-3 lg:pt-3">
            {remodeling.subcontracted.map((t) => (
              <li key={t} className="flex items-start gap-3 text-white/85">
                <span className="w-6 h-6 rounded-full bg-brand/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={13} className="text-brand" aria-hidden="true" />
                </span>
                <span>
                  {t} <span className="text-white/45">— licensed subcontractor</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-secondary">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
            Questions People Ask
          </h2>
          <div className="brand-line mb-9" />
          <dl className="divide-y divide-ink/10">
            {FAQ.map((f) => (
              <div key={f.q} className="py-6">
                <dt className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</dt>
                <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Estimate */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-4xl uppercase tracking-tight mb-4">Tell Us The Project</h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground leading-relaxed mb-6">
              Describe what you are thinking about and we will come look at it. You get a written
              scope with line-item pricing either way, and it is yours whether or not you hire us.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Remodeling work carries a <b className="text-ink">{remodeling.warrantyYears}-year
              written workmanship warranty</b>. The term is in the contract before anyone starts.
            </p>
            <a
              href={`tel:${brand.phoneRaw}`}
              className="inline-flex items-center gap-3 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors"
            >
              <Phone size={18} /> {brand.phone}
            </a>
            <p className="text-muted-foreground text-sm mt-8">
              Still need the roof looked at?{" "}
              <Link to="/services/roofing" className="text-brand-dark font-semibold link-underline">
                Roofing services <ArrowRight size={13} className="inline" />
              </Link>
            </p>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-card border border-ink/10 rounded-2xl p-6 md:p-8 shadow-sm">
              <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">Request An Estimate</h3>
              <p className="text-muted-foreground text-sm mb-6">
                Tell us what you have in mind and a team member will call to arrange a look.
              </p>
              {/* Pre-ticks the Remodeling box — someone who reached this
                  page has already told us what they want. */}
              <LeadForm defaultService="Home Remodeling" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Remodeling;
